"use client";

import React from "react";

export interface TestimonialsProps {
  title: string;
  rating?: {
    score: string;
    platform?: string; // Made optional - temporarily hiding Trustpilot
  };
  description?: string;
  testimonials: React.ReactNode[];
  footerLink?: {
    text: string;
    url: string;
    platform?: string; // Made optional - temporarily hiding Trustpilot
  };
}

export function TestimonialsSection({
  title,
  rating,
  description,
  testimonials,
  footerLink
}: TestimonialsProps) {
  return (
    <>
      <style jsx>{`
        @keyframes testimonial-scroll {
          0% {
            transform: translateX(0);
          }
          100% {
            transform: translateX(-50%);
          }
        }
        .animate-testimonial-scroll {
          animation: testimonial-scroll 60s linear infinite;
        }
        .animate-testimonial-scroll:hover {
          animation-play-state: paused;
        }
        .testimonial-container {
          scrollbar-width: thin;
          scrollbar-color: #e5e7eb #f3f4f6;
        }
        .testimonial-container::-webkit-scrollbar {
          height: 8px;
        }
        .testimonial-container::-webkit-scrollbar-track {
          background: #f3f4f6;
          border-radius: 4px;
        }
        .testimonial-container::-webkit-scrollbar-thumb {
          background: #e5e7eb;
          border-radius: 4px;
        }
        .testimonial-container::-webkit-scrollbar-thumb:hover {
          background: #d1d5db;
        }
      `}</style>
      
      <section className="py-20 bg-gradient-to-b from-white to-gray-50 overflow-hidden">
        <div className="max-w-7xl mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              {title}
            </h2>
            {rating && (
              <div className="flex items-center justify-center gap-3 mb-4">
                <div className="flex gap-0.5">
                  {[...Array(5)].map((_, i) => (
                    <svg key={i} className="w-6 h-6 text-green-500 fill-current" viewBox="0 0 20 20">
                      <path d="M10 15l-5.878 3.09 1.123-6.545L.489 6.91l6.572-.955L10 0l2.939 5.955 6.572.955-4.756 4.635 1.123 6.545z" />
                    </svg>
                  ))}
                </div>
                <span className="text-lg font-semibold text-gray-900">{rating.score}</span>
                {/* <span className="text-gray-600">on {rating.platform}</span> */}
              </div>
            )}
            {description && (
              <p className="text-gray-600 max-w-2xl mx-auto">
                {description}
              </p>
            )}
          </div>
          
          <div className="relative">
            {/* Desktop view - horizontal scroll */}
            <div className="hidden md:block overflow-x-auto testimonial-container">
              <div className="flex gap-6 animate-testimonial-scroll py-4">
                {/* First set of reviews */}
                <div className="flex gap-6 shrink-0">
                  {testimonials}
                </div>
                {/* Duplicate set for seamless loop */}
                <div className="flex gap-6 shrink-0">
                  {testimonials}
                </div>
              </div>
            </div>
            
            {/* Mobile view - stacked cards */}
            <div className="md:hidden space-y-4">
              {testimonials.slice(0, 5)}
            </div>
          </div>
          
          {footerLink && (
            <div className="text-center mt-10">
              <a
                href={footerLink.url}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 text-gray-600 hover:text-gray-900 transition-colors"
              >
                <span className="text-sm font-medium">{footerLink.text}</span>
                {/* <span className="font-bold text-[#00b67a]">{footerLink.platform}</span> */}
              </a>
            </div>
          )}
        </div>
      </section>
    </>
  );
}